/**
 * blocks.mjs — a program as text at rest, and as a machine running it.
 *
 * FOUNDER, 2026-08-14: "visual learning is much better than we just writing text. so general
 * learning in ANY domain is visual oriented, blocks etc" — then "ch4 blocks first".
 *
 * ── WHAT CHAPTER 4 ACTUALLY TURNS ON ──────────────────────────────────────────────────────────
 * Not "how to program". The ladder's own unlock line: "The difference between the text of a program
 * and the act of running it — and therefore the difference between a bug you can read and a bug you
 * can only observe." Everything here serves that one sentence and nothing else.
 *
 * The module names three things that happen at once when code runs, and this engine returns exactly
 * those three, in his words:
 *   1. reading the next line
 *   2. keeping track of values — "a slot in its memory called total" — the STATE
 *   3. doing work that can affect the outside world — the EFFECTS
 * "Without effects, running code is invisible."
 *
 * ── WHY BLOCKS CAN ONLY TEACH HALF OF SECTION 3, AND WHY THAT IS THE LESSON ───────────────────
 * Section 3 is "Syntax vs semantics — two ways to be wrong". Blocks CANNOT produce a syntax error:
 * they only fit together one way. That is not a limitation to hide — it is the truest thing this
 * instrument can say. A learner who assembles blocks, cannot mis-spell, and then watches the
 * machine happily compute a wrong answer has felt the distinction the chapter is built on, in a way
 * no paragraph delivers. So the syntax half is shown on the TEXT pane, by corrupting the text the
 * blocks compiled to and watching the machine refuse to start.
 *
 * ── IT COMPUTES, IT DOES NOT JUDGE ────────────────────────────────────────────────────────────
 * The machine reports what the program produced. It never says "wrong". A semantic error IS a
 * program running correctly — that is the entire point of the section — and an instrument that
 * flagged it would be teaching the opposite of what it is here to teach. What the lab may do is
 * COMPUTE a second number the learner can compare against, which is arithmetic, not judgement.
 */

/** The four operations. Deliberately few: this teaches what running IS, not a language. */
export const BLOCK_TYPES = Object.freeze({
  set:  { verb: 'set',      needs: ['slot', 'value'], text: (b) => `${b.slot} = ${b.value}` },
  add:  { verb: 'add',      needs: ['slot', 'value'], text: (b) => `${b.slot} = ${b.slot} + ${b.value}` },
  mul:  { verb: 'multiply', needs: ['slot', 'value'], text: (b) => `${b.slot} = ${b.slot} * ${b.value}` },
  show: { verb: 'show',     needs: ['slot'],          text: (b) => `print(${b.slot})` },
});

/**
 * HIS OWN EXAMPLE, not one I invented. Module 4 §1: "total = 240 + 180 + 90 / print(total)", and
 * the numbers are the grocery-spend example that runs through module 3 — "Monday I spent 240 on
 * groceries, then 180 on auto fare. Tuesday was quiet, just 90 on tea." The module states the
 * answer is 510, which is what the engine must produce and what the gate asserts.
 */
export const STARTER = Object.freeze([
  { op: 'set', slot: 'total', value: 240 },
  { op: 'add', slot: 'total', value: 180 },
  { op: 'add', slot: 'total', value: 90 },
  { op: 'show', slot: 'total' },
]);

export const STARTER_SOURCE = 'Code101 module 04 §1 "What code actually is" — total = 240 + 180 + 90; print(total). The three numbers are the grocery-spend example of module 03.';

/** The program AS TEXT. At rest it does nothing — that is the half of the chapter this pane is for. */
export const toText = (prog) => (prog || []).map((b) => BLOCK_TYPES[b.op]?.text(b) ?? '').join('\n');

/**
 * Run the program and return the WHOLE trace — every step, with the line that was read, the slots
 * after it, and any effect. Returned as a trace rather than a final answer because the chapter is
 * about the act of running, not the result: a UI that only showed 510 would have taught nothing the
 * text did not already say.
 */
export function run(prog) {
  const slots = {};
  const steps = [];
  const effects = [];
  (prog || []).forEach((b, i) => {
    const t = BLOCK_TYPES[b.op];
    if (!t) return;
    let effect = null;
    /* The machine reads the line, then does what it says. Reading an unset slot is NOT a crash
       here — it is treated as an empty slot, and the step says so, because "the slot does not
       exist yet" is a state a learner should see rather than an exception that ends the lesson. */
    const before = slots[b.slot];
    if (b.op === 'set') slots[b.slot] = Number(b.value);
    else if (b.op === 'add') slots[b.slot] = (before ?? 0) + Number(b.value);
    else if (b.op === 'mul') slots[b.slot] = (before ?? 0) * Number(b.value);
    else if (b.op === 'show') { effect = String(slots[b.slot] ?? '(this slot does not exist)'); effects.push(effect); }
    steps.push({
      line: i,
      text: t.text(b),
      /* A COPY of the slots at this instant, not a reference. Pushing the live object would make
         every step show the FINAL state — the trace would look like the machine knew the answer
         before it had done the work, which is the one impression this instrument must not give. */
      slots: { ...slots },
      effect,
      note: b.op === 'show' && slots[b.slot] === undefined ? 'the machine looked for a slot that was never created' : null,
    });
  });
  return { steps, slots, effects };
}

/** What the numbers in this program ADD to, regardless of what the program does with them. */
export const sumOfValues = (prog) => (prog || [])
  .filter((b) => b.op === 'set' || b.op === 'add' || b.op === 'mul')
  .reduce((t, b) => t + Number(b.value || 0), 0);

/**
 * ── THE SYNTAX HALF, SHOWN ON THE TEXT ────────────────────────────────────────────────────────
 * Blocks cannot be mis-spelled, so a syntax error is manufactured on the TEXT the blocks compiled
 * to — a bracket removed from a real line of the learner's own program — and the machine then
 * refuses to start. The message names the line, as the module's example does:
 * "SyntaxError: missing ')' at line 3".
 *
 * Returns null when the program has no line that can carry the break, rather than inventing one.
 */
export function breakSyntax(prog) {
  const lines = toText(prog).split('\n');
  const i = lines.findIndex((l) => l.includes(')'));
  if (i < 0) return null;
  const broken = lines.slice();
  broken[i] = broken[i].replace(')', '');
  return { text: broken.join('\n'), line: i + 1, error: `SyntaxError: missing ')' at line ${i + 1}`, ran: false };
}

/** The two ways to be wrong, in the module's own framing. Quoted, with the section. */
export const TWO_WRONGS = Object.freeze({
  syntax: {
    what: 'Syntax is the spelling and grammar of code.',
    consequence: 'Syntax errors prevent the machine from even starting to follow the recipe — it cannot parse the text.',
    verdict: 'Fixable. Annoying. Not deep.',
    source: 'Code101 module 04 §3 "Syntax vs semantics — two ways to be wrong"',
  },
  semantic: {
    what: 'Semantics is whether the code, even if it runs cleanly, does the right thing.',
    consequence: 'Semantic errors are far more dangerous than syntax errors, because the machine happily runs them. The output looks fine. The damage is hidden until someone checks.',
    verdict: 'Did your "sum" function actually add the numbers, or did it accidentally multiply them?',
    source: 'Code101 module 04 §3 "Syntax vs semantics — two ways to be wrong"',
  },
});

/** The three things the module says happen at once. Named here so the UI cannot rename them. */
export const THREE_THINGS = Object.freeze([
  { key: 'line', label: 'Reading the next line', quote: 'One line at a time, top to bottom.' },
  { key: 'slots', label: 'Keeping track of values', quote: 'the machine creates a slot in its memory called total and stores the number 510 in it. This running collection of slots is called the state of the program.' },
  { key: 'effects', label: 'Doing work you can see', quote: 'Without effects, running code is invisible.' },
]);
export const THREE_THINGS_SOURCE = 'Code101 module 04 §4 "What “running” actually means"';
